import {  useRef, useState } from "react";
import { ServiceWorkerMessageType } from "../typings";
import pkgJson from "../../../package.json";
import { useDedupEffect } from "shared/hooks/useDedupEffect";

interface IUseGetServiceWokerRegistrationOptions {
  pollingIntervalMs: number;
  maxRetryAttempts: number;
}
function useGetServiceWorkerRegistration(
  clientUrl: string,
  {
    pollingIntervalMs,
    maxRetryAttempts,
  }: IUseGetServiceWokerRegistrationOptions = {
    pollingIntervalMs: 2000,
    maxRetryAttempts: 40,
  },
  onFailure: (errorMessage: string) => void
) {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const retryAttempts = useRef(0);

  console.log("clientURL : ", clientUrl, " registration : ", registration);

  function getServiceWorkerRegistration() {
    navigator.serviceWorker
      .getRegistration(clientUrl)
      .then((registration) => {
        console.log(
          `registration in the getRegistration.then() for clientUrl ${clientUrl}`,
          registration
        ); 
        if (registration) {
          setRegistration(registration);
          return;
        } else if (retryAttempts.current < maxRetryAttempts) {
          setTimeout(() => {
            getServiceWorkerRegistration();
          }, pollingIntervalMs);
        } else {
          onFailure(
            `Failed to get serviceWorkerRegistration for clientUrl : ${clientUrl}`
          );
        }
      })
      .finally(() => (retryAttempts.current += 1));
  }

  useDedupEffect(() => {
    if (registration) {
      return;
    }
    if (retryAttempts.current >= maxRetryAttempts) {
      return;
    }

    getServiceWorkerRegistration();
  }, [registration]);

  return registration;
}

function useGetActiveServiceWorker(
  {
    maxRetryAttempts,
    pollingIntervalMs,
  }: { maxRetryAttempts: number; pollingIntervalMs: number },
  onFailure: (errorMessage: string) => void,
  registration?: ServiceWorkerRegistration,
  applicationVersion?: string,
  onVersion?: (version: string) => void,
  onIncorrectVersion?: (version: string) => void
) {
  const [serviceWorker, setServiceWorker] = useState<ServiceWorker>();
  const retryCount = useRef(0);
  const receivedVersion = useRef<string>("NONE RECEIVED");

  function checkServiceWorkerIsValidVersion(
    worker: ServiceWorker,
    registration: ServiceWorkerRegistration
  ) {
    if (retryCount.current > maxRetryAttempts) {
      onFailure(
        `Exceeded maximum retry attempts to check service worker is valid version for SW with scriptURL ${worker.scriptURL}. 
        \n Last received version was ${receivedVersion.current}`
      );
      return;
    }
    if (serviceWorker) {
      return;
    }
    if (!applicationVersion) {
      setServiceWorker(worker);
      return;
    }

    // set a new message channel for each of these messages. We don't persist this port because
    // we may be communicating with different SW if we are awaiting a valid
    const swMessageChannel = new MessageChannel();

    swMessageChannel.port1.onmessage = (event) => {
      switch (event.data.type) {
        case ServiceWorkerMessageType.GET_APPLICATION_VERSION: {
          const version = event.data.data.version;
          receivedVersion.current = version;
          onVersion?.(version);
          console.log("received application version in APP : ", version);
          if (version === applicationVersion) {
            setServiceWorker(worker);
          } else {
            registration.update();
            if (retryCount.current >= maxRetryAttempts) {
              onIncorrectVersion?.(version);
            }
            setTimeout(() => {
              getActiveServiceWorker(registration);
            }, pollingIntervalMs);
          }
          break;
        }

        default:
          break;
      }
    };
    console.log(
      "[ADPClient][useGetActiveServiceWorker] posting message to get application version for registration : ",
      registration
    );
    worker.postMessage(
      {
        type: ServiceWorkerMessageType.GET_APPLICATION_VERSION,
      },
      [swMessageChannel.port2]
    );
  }

  function getActiveServiceWorker(registration: ServiceWorkerRegistration) {
    if (retryCount.current > maxRetryAttempts) {
      onFailure(
        `Exceeded max retry attempts to get active service worker for registration with scope ${registration.scope}. Last received version was ${receivedVersion.current}.`
      );
      return;
    }
    retryCount.current += 1;
    //console.log("registration in interval : ", registration);
    // This means there will be a newer service worker active soon and we don't want the old one, which is currently active
    if (registration.installing || registration.waiting) {
      setTimeout(() => {
        getActiveServiceWorker(registration);
      }, pollingIntervalMs);
      return;
    }

    if (registration.active) {
      if (!applicationVersion) {
        setServiceWorker(registration.active);
        return;
      }
      checkServiceWorkerIsValidVersion(registration.active, registration);
    } else {
      // no active, installing, or waiting service workers found... try again
      setTimeout(() => {
        getActiveServiceWorker(registration);
      }, pollingIntervalMs);
    }
  }

  useDedupEffect(() => {
    //console.log("registration in useGetActiveServiceWorker : ", registration);
    if (!registration) {
      return;
    }
    if (serviceWorker) {
      return;
    }

    getActiveServiceWorker(registration);
  }, [serviceWorker, registration]);

  return serviceWorker;
}

export function useServiceWorkerPrecacheValidation(
  clientUrl: string,
  skipValidation: boolean,
  appVersion?: string,
  onVersion?: (version: string) => void,
  onIncorrectVersion?: (version: string) => void,
  onPrecacheFailed?: (manifestUrls: string[], failedUrls: string[]) => void,
  onError?: (errorMessage: string) => void,
  {
    pollingIntervalMs,
    maxRetryAttempts,
  }: { pollingIntervalMs: number; maxRetryAttempts: number } = {
    pollingIntervalMs: 2000,
    maxRetryAttempts: Infinity,
  }
) {
  const [isPrecacheComplete, setIsPrecacheComplete] = useState(false);
  const [isPrecacheFailure, setIsPrecacheFailure] = useState(false);
  const manifestUrls = useRef<string[]>([]);
  const failedUrls = useRef<string[]>([]);
  if(skipValidation){
    setIsPrecacheComplete(true);
  }
  function handleGenericFailure(errorMessage: string) {
    console.log(
      `[ADPClient][useServiceWorkerPrecacheValidation] precache failed for clientUrl ${clientUrl} with appVersion ${appVersion}. Error message below:\n `,
      errorMessage
    );
    onError?.(errorMessage);
    setIsPrecacheFailure(true);
  }

  function handlePrecacheFailure() {
    onPrecacheFailed?.(manifestUrls.current, failedUrls.current);
    setIsPrecacheFailure(true);
  }

  const swRegistration = useGetServiceWorkerRegistration(
    clientUrl,
    { pollingIntervalMs, maxRetryAttempts },
    handleGenericFailure
  );
  const activeServiceWorker = useGetActiveServiceWorker(
    { pollingIntervalMs, maxRetryAttempts },
    handleGenericFailure,
    swRegistration,
    appVersion,
    onVersion,
    onIncorrectVersion
  );

  const intervalRef = useRef<number>();
  const retryCount = useRef(0);

  function handleMessageEvent(event: MessageEvent) {
    switch (event.data.type) {
      case ServiceWorkerMessageType.PRECACHE_COMPLETE:
        console.log("[ADPClient][useServiceWorkerPrecacheValidation >> handleMessageEvent] precache complete for clientUrl : ", clientUrl);
        clearInterval(intervalRef.current);
        setIsPrecacheComplete(true);
        break;
      case ServiceWorkerMessageType.PRECACHE_NOT_COMPLETE: {
        const { manifestUrls, failedUrls } = event.data.data;
        manifestUrls.current = manifestUrls;
        failedUrls.current = failedUrls;
        console.log("[ADPClient][useServiceWorkerPrecacheValidation >> handleMessageEvent] precache NOT complete", manifestUrls?.current, failedUrls?.current)
        break;
      }
      case ServiceWorkerMessageType.PRECACHE_FAILURE:
        console.warn("[ADPClient][useServiceWorkerPrecacheValidation >> handleMessageEvent] precache failed for clientUrl : ", clientUrl);
        clearInterval(intervalRef.current);
        handlePrecacheFailure();
        break;
      default:
        break;
    }
  }
  useDedupEffect(() => {
    if (retryCount.current >= maxRetryAttempts) {
      clearInterval(intervalRef.current);
      handlePrecacheFailure();
      return;
    }
    if (!activeServiceWorker || isPrecacheComplete || isPrecacheFailure) {
      // In case there's no SW that ever becomes active, we will increment retry count here as well
      // to indicate that there was a failure after too many retries
      retryCount.current += 1;
      clearInterval(intervalRef.current);
      return;
    }
    if (intervalRef.current) {
      return;
    }

    intervalRef.current = window.setInterval(() => {
      if (retryCount.current > maxRetryAttempts) {
        clearInterval(intervalRef.current);
        handlePrecacheFailure();
        return;
      }

      retryCount.current += 1;
      console.log(
        "[ADPClient][useServiceWorkerPrecacheValidation] posting poll precache state message for sw : ",
        activeServiceWorker,
        " :: registration : ",
        swRegistration
      );
      const channel = new MessageChannel();
      channel.port1.onmessage = handleMessageEvent;
      activeServiceWorker.postMessage(
        {
          type: ServiceWorkerMessageType.POLL_PRECACHE_STATE,
        },
        [channel.port2]
      );
    }, pollingIntervalMs);
  }, [
    activeServiceWorker,
    isPrecacheComplete,
    isPrecacheFailure,
    handleMessageEvent,
  ]);

  return [isPrecacheComplete, isPrecacheFailure];
}
